import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { uploadFile } from '../../Services';
import { UploaderThemesEnum, DefaultImagesEnum } from '../../Enums';
import { Spinner, LoadableImageComponant } from '../../Components';
import { InputThemeComponent } from '../../Components/UploaderComponent/UploaderThemes/InputThemeComponent/InputThemeComponent';
import { CircleThemeComponent } from '../../Components/UploaderComponent/UploaderThemes/CircleThemeComponent/CircleThemeComponent';
import { BoxThemeComponent } from '../../Components/UploaderComponent/UploaderThemes/BoxThemeComponent/BoxThemeComponent';
import { GalleryShowThemeComponent } from '../../Components/UploaderComponent/UploaderThemes/GalleryShowThemeComponent/GalleryShowThemeComponent';
import { getDownloadableLink } from '../../Helper';
import { getMimeTypeHandler, isImageFromURL } from '../../Utils';
import { showError  } from '../../Helper';

export const ImageUploader = ({
  wrapperClasses,
  uploaderClasses,
  counterClasses,
  inputClasses,
  labelClasses,
  accept,
  multiple,
  initUploadedFiles,
  chipsDisabled,
  Disabledimg,
  translationPath,
  parentTranslationPath,
  translationPathShared,
  uploadedChanged,
  allFilesChanged,
  titleText,
  labelValue,
  isDisabled,
  idRef,
  defaultImage,
  viewUploadedFilesCount,
  dropHereText,
  uploaderTheme,
  chipHandler,
  WithoutDefaultImg,
  openGallery,
  isDocuments,
  setIsUploading,
  isUploading,
}) => {
  const { t } = useTranslation([parentTranslationPath, 'Shared']);
  const uploadRef = useRef(null);
  const [allFiles, setAllFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isOpenGallery, setIsOpenGallery] = useState(false);

  const uploadHandler = async (files) => {
    setIsUploading(true);
    let response = null ; 
    const uploaded = await Promise.all(
      files.map(async (item) => {
        try {
           response = await uploadFile({ file: item.file })
            return { ...item, status: "success", uuid: response.uuid };
       
        } catch {
          setIsUploading(false);
          return { ...item, status: "failed" };
        }
        
      })
    );
    if (response) {
      const newList = [
        ...allFiles.filter(
          (f) => !uploaded.some((u) => u.fileName === f.fileName)
        ),
        ...uploaded,
      ];
      setUploadedFiles(newList);
      setAllFiles(newList);
      uploadedChanged(newList);
    } else if(!response) {
      showError(
        t(`${translationPathShared}failed-to-upload-images`)
      );
    }   
    setIsUploading(false);
  };
  const dropHandler = (event) => {
    event.preventDefault();
    if (isDisabled) return;
    setIsDragOver(false);
    let filesToUpload = Object.values(event.dataTransfer.files);
    if (accept.includes('image')) {
      filesToUpload = filesToUpload.filter((item) => item.type.includes('image'));
    }

    if (filesToUpload.length === 0) {
      showError(
        t(`${translationPathShared}files-that-are-not-images-cannot-be-uploaded`)
      );
      return;
    }

    const files = filesToUpload
      .filter((file) => !allFiles.some((f) => f.fileName === file.name))
      .map((file, index) => ({
        id: allFiles.length + index,
        uuid: null,
        fileName: file.name,
        size: file.size,
        type: file.type,
        file,
        status: 'uploading',
      }));

    if (files.length === 0) return; // No new files to upload

    const newList = [...allFiles, ...files];
    setAllFiles(newList);
    uploadHandler(files);
  };

  const fileDeleted = useCallback(
    (item) => () => {
      const newUploadedFiles = uploadedFiles.filter((file) => file.uuid !== item.uuid);
      const newAllFiles = allFiles.filter((file) => file.uuid !== item.uuid);
      setUploadedFiles(newUploadedFiles);
      setAllFiles(newAllFiles);
      uploadedChanged(newUploadedFiles);
    },
    [uploadedFiles, allFiles, uploadedChanged]
  );

  const inputChanged = (event) => {
    if (!event.target.value) return;

    let files = [];
    if (multiple) {
      files = Object.values(event.target.files)
        .filter((file) => file.type && file.type.includes('image'))
        .filter((file) => !allFiles.some((f) => f.fileName === file.name))
        .map((file, index) => ({
          id: allFiles.length + index,
          uuid: null,
          fileName: file.name,
          size: file.size,
          type: file.type,
          file,
          status: 'uploading',
        }));

      if (files.length !== event.target.files.length) {
        showError(
          t(`${translationPathShared}files-that-are-not-images-cannot-be-uploaded`)
        );
      }
      if (files.length === 0) {
        event.target.value = null;
        return;
      }
    } else {
      const file = event.target.files[0];
      if (file.type.includes('image') && !allFiles.some((f) => f.fileName === file.name)) {
        files = [
          {
            id: allFiles.length,
            uuid: null,
            fileName: file.name,
            size: file.size,
            type: file.type,
            file,
            status: 'uploading',
          },
        ];
      } else {
        showError(
          t(`${translationPathShared}files-that-are-not-images-cannot-be-uploaded`)
        );
        event.target.value = null;
        return;
      }
    }

    const newList = [...allFiles, ...files];
    setAllFiles(newList);
    uploadHandler(files);
    event.target.value = null;
  };

  const downloadButtonClicked = (item) => {
    try {
      const link = document.createElement('a');
      link.setAttribute('download', item.fileName || item.documentName);
      link.href = getDownloadableLink(item.uuid || item.documentId);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {}
  };

  const chipClicked = useCallback(() => {
    setIsOpenGallery(true);
  }, []);

  const checkImagesHandler = async () => {
    try {
      setIsUploading(true);
      const initUploadedImageFiles = (
        await Promise.all(
          initUploadedFiles
            ?.filter((item) => !item.isChecked)
            .map(async (item) => {
              const isImageFile = getMimeTypeHandler(item.fileName).isImage;
              item.isChecked = true;
              if (isImageFile) return item;
              const link = getDownloadableLink(item.uuid);
              const isImage = await isImageFromURL(link);
              return isImage ? item : null;
            })
        )
      ).filter((item) => item);
      const newFiles = initUploadedImageFiles.filter(
        (item) => !allFiles.some((f) => f.uuid === item.uuid)
      );
      if (newFiles.length > 0) {
        setAllFiles([...allFiles, ...newFiles]);
        setUploadedFiles([...uploadedFiles, ...newFiles]);
      }
      setIsUploading(false);
    } catch (error) {
      showError('something went wrong');
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (initUploadedFiles && initUploadedFiles.length > 0) {
      const newFiles = initUploadedFiles.filter(
        (item) => !allFiles.some((f) => f.uuid === item.uuid)
      );
      if (newFiles.length > 0) {
        setUploadedFiles([...uploadedFiles, ...newFiles]);
        setAllFiles([...allFiles, ...newFiles]);
      }
      if (initUploadedFiles.some((item) => !item.isChecked)) {
        checkImagesHandler();
      }
    } else if (initUploadedFiles && initUploadedFiles.length === 0) {
      setUploadedFiles([]);
      setAllFiles([]);
    }
  }, [initUploadedFiles?.length]);

  useEffect(() => {
    setIsOpenGallery(openGallery || false);
    if (allFilesChanged) {
      allFilesChanged(allFiles);
    }
  }, [allFiles, allFilesChanged, openGallery]);

  const removeButtonClicked = (item) => {
    fileDeleted(item)();
  };


  return (
    <div className={wrapperClasses}>
      {labelValue && (
        <label
          htmlFor={idRef}
          className={`label-wrapper ${labelClasses}${isDisabled ? ' disabled' : ''}`}
        >
          {t(`${translationPath}${labelValue}`)}
        </label>
      )}
      <input
        ref={uploadRef}
        type='file'
        className={inputClasses}
        multiple={multiple}
        accept={accept}
        onChange={inputChanged}
        disabled={isDisabled}
      />
      <div
        className={uploaderClasses}
        onDragOver={(event) => {
          event.preventDefault();
          if (isDisabled) return;
          if (!isDragOver) setIsDragOver(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setIsDragOver(false);
        }}
        onDrop={dropHandler}
      >
        {uploaderTheme === UploaderThemesEnum.input.key && (
          <InputThemeComponent
            allFiles={allFiles}
            isDragOver={isDragOver}
            translationPathShared={translationPathShared}
            fileDeleted={fileDeleted}
            chipClicked={chipHandler || chipClicked || undefined}
            uploadRef={uploadRef}
            multiple={multiple}
            accept={accept}
            idRef={idRef}
            chipsDisabled={chipsDisabled}
            isDisabled={isDisabled}
          />
        )}
        {uploaderTheme === UploaderThemesEnum.circle.key && (
          <CircleThemeComponent
            allFiles={allFiles}
            defaultImage={defaultImage}
            isDragOver={isDragOver}
            translationPathShared={translationPathShared}
            fileDeleted={fileDeleted}
            uploadRef={uploadRef}
          />
        )}
        {uploaderTheme === UploaderThemesEnum.box.key && (
          <BoxThemeComponent
            file={(allFiles.length > 0 && allFiles[0]) || undefined}
            defaultImage={defaultImage}
            isDragOver={isDragOver}
            WithoutDefaultImg={WithoutDefaultImg}
            translationPathShared={translationPathShared}
            fileDeleted={fileDeleted}
            uploadRef={uploadRef}
            dropHereText={dropHereText}
            multiple={multiple}
            accept={accept}
          />
        )}
        {uploaderTheme === UploaderThemesEnum.GalleryShow.key && (
          <GalleryShowThemeComponent
            allFiles={allFiles}
            isDragOver={isDragOver}
            translationPathShared={translationPathShared}
            fileDeleted={fileDeleted}
            uploadRef={uploadRef}
            multiple={multiple}
            accept={accept}
            idRef={idRef}
            Disabledimg={Disabledimg}
            isDisabled={isDisabled}
          />
        )}
        {viewUploadedFilesCount && (
          <span className={counterClasses}>
            {`${allFiles.length} ${
              (accept &&
                accept.includes('image') &&
                t(
                  `${translationPathShared}${
                    (allFiles.length > 1 && 'images-uploaded') || 'image-uploaded'
                  }`
                )) ||
              t(
                `${translationPathShared}${
                  (allFiles.length > 1 && 'files-uploaded') || 'file-uploaded'
                }`
              )
            }`}
          </span>
        )}
      </div>
      <div className='GalleryComponent'>
        <Spinner isActive={isUploading} />
        {isDocuments &&
          allFiles.length > 0 &&
          allFiles.map((image) => (
            <span className='All-documents document-card' key={image.uuid || image.fileName}>
              <LoadableImageComponant
                classes='cover-image'
                src={image.uuid && getDownloadableLink(image.uuid)}
                alt={t(`${translationPath}cover-image`)}
                width={150}
                height={100}
              />
              <div className='file-icons-container-with-download-icon'>
                <span
                  className='mdi mdi-delete-circle-outline c-warning'
                  onClick={() => removeButtonClicked(image)}
                />
                <span
                  className='mdi mdi-download-circle-outline c-primary'
                  onClick={() => downloadButtonClicked(image)}
                />
              </div>
            </span>
          ))}
      </div>
    </div>
  );
};

ImageUploader.propTypes = {
  initUploadedFiles: PropTypes.instanceOf(Array),
  wrapperClasses: PropTypes.string,
  labelClasses: PropTypes.string,
  labelValue: PropTypes.string,
  uploaderClasses: PropTypes.string,
  idRef: PropTypes.string,
  inputClasses: PropTypes.string,
  translationPath: PropTypes.string,
  parentTranslationPath: PropTypes.string,
  translationPathShared: PropTypes.string,
  accept: PropTypes.string,
  counterClasses: PropTypes.string,
  titleText: PropTypes.string,
  uploaderTheme: PropTypes.oneOf(
    Object.values(UploaderThemesEnum).map((item) => item.key)
  ),
  multiple: PropTypes.bool,
  chipsDisabled: PropTypes.func,
  Disabledimg: PropTypes.func,
  chipHandler: PropTypes.func,
  uploadedChanged: PropTypes.func,
  allFilesChanged: PropTypes.func,
  isDisabled: PropTypes.bool,
  WithoutDefaultImg: PropTypes.bool,
  viewUploadedFilesCount: PropTypes.bool,
  defaultImage: PropTypes.string,
  dropHereText: PropTypes.string,
};

ImageUploader.defaultProps = {
  initUploadedFiles: [],
  wrapperClasses: 'uploader-wrapper',
  labelClasses: '',
  uploaderClasses: 'uploader-container',
  counterClasses: 'counter-text',
  inputClasses: 'file-input',
  idRef: 'uploaderChipRef',
  translationPath: '',
  parentTranslationPath: '',
  translationPathShared: 'Shared:uploaderComponent.',
  accept:
    'image/*,application/pdf,application/msword,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  titleText: undefined,
  chipHandler: undefined,
  labelValue: undefined,
  uploaderTheme: UploaderThemesEnum.input.key,
  multiple: false,
  WithoutDefaultImg: false,
  chipsDisabled: () => false,
  Disabledimg: () => false,
  allFilesChanged: undefined,
  uploadedChanged: undefined,
  isDisabled: false,
  viewUploadedFilesCount: false,
  defaultImage: DefaultImagesEnum.corporate.key,
  dropHereText: 'drop-here',
  setIsUploading: () => {},
};